import { useEffect, useState, type HTMLAttributes } from "react"
import { cn } from "cn"

import IconMinus from "@/components/icons/minus"
import IconPlus from "@/components/icons/plus"
import { IconButton } from "./icon-button"

interface NumberStepperProps extends HTMLAttributes<HTMLDivElement> {
  min?: number
  max?: number
  value?: number
  displayValue?: string | number
  onValueChange?: (value: number) => void
}

export const NumberStepper = ({
  className,
  min = 0,
  max,
  value = 0,
  displayValue,
  onValueChange,
  children,
  ...props
}: NumberStepperProps) => {
  const [currentValue, setCurrentValue] = useState(value)

  useEffect(() => {
    setCurrentValue(value)
  }, [value])

  const handleIncrease = () => {
    if (max === undefined || currentValue < max) {
      const newValue = currentValue + 1
      setCurrentValue(newValue)
      if (onValueChange) {
        onValueChange(newValue)
      }
    }
  }

  const handleDecrease = () => {
    if (currentValue > min) {
      const newValue = currentValue - 1
      setCurrentValue(newValue)
      if (onValueChange) {
        onValueChange(newValue)
      }
    }
  }

  return (
    <div
      className={cn("flex w-26 items-center justify-between", className)}
      {...props}
    >
      <IconButton
        variant="outline"
        icon={IconMinus}
        size="sm"
        onClick={handleDecrease}
        disabled={currentValue <= min}
        label="decrease value"
      />
      {children
        ? children
        : displayValue === undefined
          ? currentValue
          : displayValue}
      <IconButton
        variant="outline"
        icon={IconPlus}
        size="sm"
        onClick={handleIncrease}
        disabled={max !== undefined && currentValue >= max}
        label="increase value"
      />
    </div>
  )
}
